import React from "react";
import { Box, Typography } from "@mui/material";
import { productBrand } from "../../productBrand";

export const LINKEDIN_ASSET_WIDTH_PX = 1080;
export const LINKEDIN_ASSET_HEIGHT_PX = 1350;

export const NAVY = "#FFFFFF";
export const BRAND = "#7056EB";
export const MUTED = "rgba(255, 255, 255, 0.72)";
export const PAGE_BG = "#1a1a1a";
export const CARD_BG = "rgba(33, 35, 45, 0.7)";
export const CARD_BORDER = "rgba(255, 255, 255, 0.1)";
export const RADIUS = "12px";
export const SERIF = '"DM Serif Text", serif';
export const SANS = '"DM Sans", sans-serif';

export const LinkedInAssetCanvas: React.FC<{
  children: React.ReactNode;
  height?: number;
}> = ({ children, height = LINKEDIN_ASSET_HEIGHT_PX }) => (
  <Box
    data-ranking-card
    sx={{
      width: LINKEDIN_ASSET_WIDTH_PX,
      height,
      boxSizing: "border-box",
      bgcolor: PAGE_BG,
      position: "relative",
      overflow: "hidden",
      px: "48px",
      py: "48px",
      display: "flex",
      flexDirection: "column",
      color: NAVY,
    }}
  >
    <Box aria-hidden sx={{ position: "absolute", inset: 0, zIndex: 0 }}>
      <Box
        component="img"
        alt=""
        src="/ranking-bg.png"
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />
      <Box
        sx={{ position: "absolute", inset: 0, bgcolor: "rgba(0,0,0,0.05)" }}
      />
    </Box>
    <Box
      sx={{
        position: "relative",
        zIndex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        flex: 1,
        minHeight: 0,
      }}
    >
      {children}
    </Box>
  </Box>
);

export const AssetKicker: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Typography
    sx={{
      m: 0,
      mb: "14px",
      fontFamily: SANS,
      fontWeight: 600,
      fontSize: 15,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: MUTED,
    }}
  >
    {children}
  </Typography>
);

export const AssetTitle: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Typography
    component="h1"
    sx={{
      m: 0,
      fontFamily: SERIF,
      fontWeight: 400,
      fontSize: 58,
      lineHeight: 1.08,
      color: NAVY,
    }}
  >
    {children}
  </Typography>
);

export const Card: React.FC<{
  children: React.ReactNode;
  sx?: React.ComponentProps<typeof Box>["sx"];
}> = ({ children, sx }) => (
  <Box
    sx={{
      px: "28px",
      py: "22px",
      bgcolor: CARD_BG,
      color: NAVY,
      borderRadius: RADIUS,
      border: `1px solid ${CARD_BORDER}`,
      ...((sx as object) ?? {}),
    }}
  >
    {children}
  </Box>
);

export const CardLabel: React.FC<{
  children: React.ReactNode;
  sx?: React.ComponentProps<typeof Typography>["sx"];
}> = ({ children, sx }) => (
  <Typography
    sx={{
      m: 0,
      mb: "28px",
      fontFamily: SANS,
      fontWeight: 600,
      fontSize: 16,
      lineHeight: 1.2,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: MUTED,
      ...((sx as object) ?? {}),
    }}
  >
    {children}
  </Typography>
);

/** Livestream default is 44px with 12px corners — keep that ratio at other sizes. */
const CHANNEL_AVATAR_DEFAULT_SIZE = 44;
const CHANNEL_AVATAR_RADIUS_PX = 12;

export const ChannelAvatar: React.FC<{
  src?: string;
  accentColor: string;
  size?: number;
  sx?: React.ComponentProps<typeof Box>["sx"];
}> = ({ src, accentColor, size = CHANNEL_AVATAR_DEFAULT_SIZE, sx }) => {
  const borderRadius = Math.max(
    4,
    Math.round((CHANNEL_AVATAR_RADIUS_PX / CHANNEL_AVATAR_DEFAULT_SIZE) * size),
  );
  return (
    <Box
      component={src ? "img" : "div"}
      src={src || undefined}
      alt=""
      sx={{
        width: size,
        height: size,
        borderRadius: `${borderRadius}px`,
        objectFit: "cover",
        overflow: "hidden",
        bgcolor: "rgba(255, 255, 255, 0.08)",
        border: `2px solid ${accentColor}`,
        flexShrink: 0,
        ...((sx as object) ?? {}),
      }}
    />
  );
};

export type DataTableHeader = {
  label: React.ReactNode;
  /** When true, prefix with ↓ and highlight as the sort column. */
  sorted?: boolean;
  align?: "left" | "right";
  /** Explicit CSS grid-column value, e.g. "1 / 3" for avatar+name. */
  gridColumn?: string | number;
};

export type DataTableRow = {
  key: string;
  /** Optional first-column node (e.g. ranking number). */
  leading?: React.ReactNode;
  avatar?: React.ReactNode;
  name: React.ReactNode;
  meta?: React.ReactNode;
  cells: React.ReactNode[];
};

export const DataTable: React.FC<{
  columns: string;
  headers: DataTableHeader[];
  rows: DataTableRow[];
  /** Grid column index for the name/meta text (default 2 = after avatar). */
  nameColumn?: number;
  /** First numeric cell's grid column (default nameColumn + 1). */
  firstCellColumn?: number;
  rowPy?: string | number;
  /** Fixed row height in px; when set, overrides rowPy for vertical sizing. */
  rowHeight?: number;
  nameFontSize?: number;
  metaFontSize?: number;
  /** Gap between program name and channel meta (px). */
  nameMetaGap?: number;
  /** Header cells align to the bottom by default so subtitles share a baseline. */
  headerAlign?: "start" | "end";
  /**
   * When true, name + metrics share one grid row and meta sits on a second row
   * under the name so tall meta (e.g. placements) does not throw off vertical alignment.
   */
  metaBelow?: boolean;
  /** When set, name stack and metrics match this height so they center with the avatar. */
  avatarSize?: number;
}> = ({
  columns,
  headers,
  rows,
  nameColumn = 2,
  firstCellColumn,
  rowPy = "14px",
  rowHeight,
  nameFontSize = 22,
  metaFontSize = 14,
  nameMetaGap = 2,
  headerAlign = "end",
  metaBelow = false,
  avatarSize,
}) => {
  const cellStart = firstCellColumn ?? nameColumn + 1;
  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: columns,
          columnGap: "12px",
          alignItems: headerAlign,
          pb: "10px",
          borderBottom: `1px solid ${CARD_BORDER}`,
        }}
      >
        {headers.map((header, index) => {
          const isSorted = Boolean(header.sorted);
          const align = header.align ?? (index === 0 ? "left" : "right");
          return (
            <Typography
              key={index}
              sx={{
                gridColumn: header.gridColumn ?? (index === 0 ? "1 / 3" : index + 2),
                m: 0,
                fontFamily: SANS,
                fontWeight: isSorted ? 700 : 600,
                fontSize: 14,
                lineHeight: 1.15,
                color: isSorted ? NAVY : MUTED,
                textAlign: align,
              }}
            >
              {isSorted ? "↓ " : null}
              {header.label}
            </Typography>
          );
        })}
      </Box>
      {rows.map((row, index) => {
        const hasMetaBelow = metaBelow && row.meta != null;
        const nameTypography = (
          <Typography
            sx={{
              m: 0,
              fontFamily: SANS,
              fontWeight: 600,
              fontSize: nameFontSize,
              lineHeight: 1.1,
              color: NAVY,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.name}
          </Typography>
        );
        const metaTypography = row.meta != null ? (
          <Typography
            component="div"
            sx={{
              m: 0,
              fontFamily: SANS,
              fontWeight: 500,
              fontSize: metaFontSize,
              lineHeight: 1,
              color: MUTED,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.meta}
          </Typography>
        ) : null;
        return (
          <Box
            key={row.key}
            sx={{
              display: "grid",
              gridTemplateColumns: columns,
              gridTemplateRows: hasMetaBelow ? "auto auto" : "auto",
              columnGap: "12px",
              rowGap: hasMetaBelow ? `${nameMetaGap}px` : 0,
              alignItems: "center",
              py: rowHeight != null ? 0 : rowPy,
              height: rowHeight,
              boxSizing: "border-box",
              borderBottom:
                index === rows.length - 1 ? "none" : `1px solid ${CARD_BORDER}`,
            }}
          >
            {row.leading}
            {row.avatar != null ? (
              <Box
                sx={{
                  gridRow: 1,
                  alignSelf: "center",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {row.avatar}
              </Box>
            ) : null}
            {hasMetaBelow ? (
              <>
                <Box sx={{ gridColumn: nameColumn, gridRow: 1, minWidth: 0, alignSelf: "center" }}>
                  {nameTypography}
                </Box>
                <Box sx={{ gridColumn: nameColumn, gridRow: 2, minWidth: 0 }}>
                  {metaTypography}
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  gridColumn: nameColumn,
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  gap: `${nameMetaGap}px`,
                  height: avatarSize != null ? avatarSize : "auto",
                  alignSelf: "center",
                }}
              >
                {nameTypography}
                {metaTypography}
              </Box>
            )}
            {row.cells.map((cell, cellIndex) => (
              <Box
                key={cellIndex}
                sx={{
                  gridColumn: cellStart + cellIndex,
                  gridRow: 1,
                  alignSelf: "center",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  height: avatarSize != null ? avatarSize : "auto",
                  m: 0,
                  fontFamily: SANS,
                  fontWeight: cellIndex === 0 ? 700 : 600,
                  fontSize: nameFontSize,
                  lineHeight: 1.1,
                  color: NAVY,
                  textAlign: "right",
                  fontVariantNumeric: "tabular-nums",
                  justifySelf: "end",
                }}
              >
                {cell}
              </Box>
            ))}
          </Box>
        );
      })}
    </>
  );
};

export const MethodologyFooter: React.FC<{
  children: React.ReactNode;
  /** Slightly tighter type for denser LinkedIn assets. */
  compact?: boolean;
}> = ({ children, compact = false }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "space-between",
      gap: compact ? "40px" : "28px",
    }}
  >
    <Typography
      component="div"
      sx={{
        flex: 1,
        minWidth: 0,
        fontFamily: SANS,
        fontSize: compact ? 13 : 15,
        lineHeight: compact ? 1.35 : 1.4,
        color: MUTED,
      }}
    >
      <Box
        component="span"
        sx={{
          fontWeight: 600,
          color: NAVY,
          borderBottom: `4px solid ${BRAND}`,
          lineHeight: 1.15,
        }}
      >
        Metodología
      </Box>
      <Box component="span" sx={{ fontWeight: 500 }}>
        {children}
      </Box>
    </Typography>
      <Box
      component="img"
      src={productBrand.wordmarkLightSrc}
      alt={productBrand.name}
      sx={{
        height: 36,
        width: "auto",
        display: "block",
        flexShrink: 0,
        transform: "translateY(5px)",
      }}
    />
  </Box>
);
